// La fase 4 de la generación del informe (documento) sobre TanStack Query: las
// condiciones de la emisión, las versiones emitidas, el formato de salida y la
// revisión de calidad (con polling mientras corre).

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { informeKey } from "@/hooks/useInformes";
import {
  cambiarFormatoRequest,
  correrCalidadRequest,
  emitirRequest,
  fetchCalidad,
  fetchCondiciones,
  fetchEmisiones,
  intervaloDeLaCalidad,
  resolverObservacionRequest,
  type CalidadDelInforme,
} from "@/lib/emision";

export const condicionesKey = (informeId: number) => ["generacion", informeId, "emision", "condiciones"] as const;
export const emisionesKey = (informeId: number) => ["generacion", informeId, "emision", "versiones"] as const;
export const calidadKey = (informeId: number) => ["generacion", informeId, "calidad"] as const;

const valido = (informeId: number) => Number.isFinite(informeId) && informeId > 0;

/**
 * Lo que bloquea la emisión depende de TODO el expediente, así que no hay un
 * escritor que pueda avisar por sí solo. Mientras la pantalla está montada,
 * cualquier acción que guarde con éxito (validar una sección, responder una
 * duda, fijar un precio…) las marca como viejas, y entrar a la fase 4 las relee
 * (cubre lo que cambia por trabajos en segundo plano, como una regeneración).
 */
export function useCondiciones(informeId: number, habilitada = true, mirando = false) {
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: condicionesKey(informeId),
    queryFn: () => fetchCondiciones(informeId),
    enabled: habilitada && valido(informeId),
  });

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe((evento) => {
        if (evento.type === "updated" && evento.action.type === "success") {
          void queryClient.invalidateQueries({ queryKey: condicionesKey(informeId) });
        }
      }),
    [informeId, queryClient],
  );

  const miraba = useRef(mirando);

  useEffect(() => {
    if (mirando && !miraba.current) void queryClient.invalidateQueries({ queryKey: condicionesKey(informeId) });
    miraba.current = mirando;
  }, [mirando, informeId, queryClient]);

  return consulta;
}

export function useEmisiones(informeId: number, habilitada = true) {
  return useQuery({
    queryKey: emisionesKey(informeId),
    queryFn: () => fetchEmisiones(informeId),
    enabled: habilitada && valido(informeId),
  });
}

/** Emitir congela una versión: se releen las versiones y las condiciones (las advertencias quedan anotadas). */
export function useEmitir(informeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => emitirRequest(informeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emisionesKey(informeId) });
      void queryClient.invalidateQueries({ queryKey: condicionesKey(informeId) });
    },
  });
}

/** Cambiar el formato refresca la ficha del informe, que es de donde la pantalla lo lee. */
export function useCambiarFormato(informeId: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (formatoId: number) => cambiarFormatoRequest(informeId, formatoId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: informeKey(informeId) });
    },
  });
}

/**
 * La última revisión de calidad. Sondea cada ~3 s mientras corre y, al terminar,
 * suelta las condiciones: es lo que decide si el documento quedó revisado.
 */
export function useCalidad(informeId: number, habilitada = true) {
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: calidadKey(informeId),
    queryFn: () => fetchCalidad(informeId),
    enabled: habilitada && valido(informeId),
    refetchInterval: (query) => intervaloDeLaCalidad(query.state.data?.corrida),
  });

  const estado = consulta.data?.corrida?.estado;
  const anterior = useRef(estado);

  useEffect(() => {
    if (anterior.current === "en_curso" && estado !== undefined && estado !== "en_curso") {
      void queryClient.invalidateQueries({ queryKey: condicionesKey(informeId) });
    }
    anterior.current = estado;
  }, [estado, informeId, queryClient]);

  return consulta;
}

/**
 * Cuando una revisión cambia lo que bloquea la emisión (terminó, o se decidió
 * una observación), las condiciones se vuelven a leer.
 */
function useAlCambiarLaCalidad(informeId: number) {
  const queryClient = useQueryClient();

  return (calidad?: CalidadDelInforme) => {
    if (calidad) queryClient.setQueryData(calidadKey(informeId), calidad);
    else void queryClient.invalidateQueries({ queryKey: calidadKey(informeId) });
    void queryClient.invalidateQueries({ queryKey: condicionesKey(informeId) });
  };
}

export function useCorrerCalidad(informeId: number) {
  const alCambiar = useAlCambiarLaCalidad(informeId);

  return useMutation({
    mutationFn: () => correrCalidadRequest(informeId),
    onSuccess: (calidad) => alCambiar(calidad),
  });
}

export function useResolverObservacion(informeId: number) {
  const alCambiar = useAlCambiarLaCalidad(informeId);

  return useMutation({
    mutationFn: ({
      observacionId,
      estado,
      motivo,
    }: {
      observacionId: number;
      estado: "abierta" | "corregida" | "descartada";
      motivo?: string;
    }) => resolverObservacionRequest(informeId, observacionId, estado, motivo),
    // La respuesta trae la observación y el resumen, no la lista entera: se relee.
    onSuccess: () => alCambiar(),
  });
}
